import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { useNotifications, useUnreadCount } from '../lib/queries';
import { Bell, BellOff, CalendarDays, CalendarPlus, CheckCheck, ClipboardList } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

import { toast } from 'sonner';

const TYPE_META = {
  task_assigned: { icon: ClipboardList, tone: 'tone-blue' },
  task_completed: { icon: CheckCheck, tone: 'tone-green' },
  event_status_changed: { icon: CalendarDays, tone: 'tone-violet' },
  event_created: { icon: CalendarPlus, tone: 'tone-amber' },
  default: { icon: Bell, tone: 'tone-grey' }
};

const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  // Cached and shared across page changes, so the bell no longer refetches
  // every time you navigate. The count refreshes every 30s while the tab is open.
  const { data: notifications = [] } = useNotifications({ enabled: !!user });
  const { data: unreadCount = 0 } = useUnreadCount({
    enabled: !!user,
    refetchInterval: 30000,
  });

  const handleMarkAsRead = async (notificationId) => {
    try {
      // Any successful write refreshes the cached notifications automatically.
      await api.put(`/notifications/${notificationId}/read`);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.is_read) {
      await handleMarkAsRead(notif.id);
    }
    
    // Navigate to related page
    setIsOpen(false);
    
    if (notif.type === 'task_assigned' || notif.type === 'task_completed') {
      // Navigate to My Tasks for team members, or to event details for admin/media_head
      if (user?.role === 'team_member') {
        navigate('/my-tasks');
      } else if (notif.related_id) {
        // Try to get task and navigate to event
        try {
          const taskRes = await api.get(`/tasks/${notif.related_id}`);
          if (taskRes.data?.event_id) {
            navigate(`/events/${taskRes.data.event_id}`);
          } else {
            navigate('/tasks');
          }
        } catch {
          navigate('/tasks');
        }
      }
    } else if (notif.type === 'event_status_changed' || notif.type === 'event_created') {
      if (notif.related_id) {
        navigate(`/events/${notif.related_id}`);
      } else {
        navigate('/events');
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/notifications/mark-all-read');
      toast.success('All notifications marked as read');
      setIsOpen(false);
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const unread = notifications.filter((n) => !n.is_read);
  const earlier = notifications.filter((n) => n.is_read);

  const renderItem = (notif) => {
    const meta = TYPE_META[notif.type] || TYPE_META.default;
    const Icon = meta.icon;
    return (
      <li key={notif.id}>
        <button
          type="button"
          data-testid={`notification-${notif.id}`}
          onClick={() => handleNotificationClick(notif)}
          className={`notif-item ${notif.is_read ? '' : 'is-unread'}`}
        >
          <span className={`notif-icon ${meta.tone}`}>
            <Icon size={16} strokeWidth={1.8} />
          </span>
          <span className="notif-body">
            <span className="notif-top">
              <strong>{notif.title}</strong>
              <time dateTime={notif.created_at}>{timeAgo(notif.created_at)}</time>
            </span>
            <span className="notif-message">{notif.message}</span>
          </span>
          {!notif.is_read && <i className="notif-dot" aria-label="Unread" />}
        </button>
      </li>
    );
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
          aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        >
          <Bell className="w-5 h-5 text-slate-700" />
          {unreadCount > 0 && (
            <span className="notif-badge" data-testid="notification-count">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={10} className="notif-panel">
        <div className="notif-head">
          <h3>
            Notifications
            {unreadCount > 0 && <span className="count-pill">{unreadCount} new</span>}
          </h3>
          {unreadCount > 0 && (
            <button type="button" className="notif-mark-all" onClick={handleMarkAllRead}>
              <CheckCheck size={14} />
              Mark all read
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="notif-empty">
            <span>
              <BellOff size={22} strokeWidth={1.7} />
            </span>
            <strong>You're all caught up</strong>
            <p>New task and event updates will show up here.</p>
          </div>
        ) : (
          <div className="notif-scroll">
            {unread.length > 0 && (
              <>
                <p className="notif-group">New</p>
                <ul>{unread.map(renderItem)}</ul>
              </>
            )}
            {earlier.length > 0 && (
              <>
                <p className="notif-group">Earlier</p>
                <ul>{earlier.map(renderItem)}</ul>
              </>
            )}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
